import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { PRESENCE_HEARTBEAT_MS, PRESENCE_TTL_MS, type Issue, type IssueResolution, type ResolutionMethod, type RunLiveEvent, type RunParticipant, type RunPresenceEntry } from '@qcbot/shared';
import { fetchCollab, fetchIssues, fetchMyUserId, inviteCollaborator, removeCollaborator, setPresence, subscribeToRunLive, updateIssueStatus } from './api';
import { participantColor } from './components/UserAvatar';

interface TrackedPresence extends RunPresenceEntry {
  /** Local receipt time — compared against the TTL so server/client clock skew can't matter. */
  receivedAt: number;
}

/** What IssueList needs to show other people's activity and to record resolutions. */
export interface IssueCollab {
  me: string | undefined;
  /** The issue I'm currently working on, if any. */
  myClaim: string | null;
  /** Everyone else's fresh claims on this issue. */
  claimedByOthers: (issueId: string) => RunPresenceEntry[];
  /** The unique colour assigned to a participant, shared by their avatar and issue border. */
  colorOf: (userId: string) => string;
  claim: (issueId: string | null) => void;
  resolve: (issueId: string, method: ResolutionMethod) => Promise<void>;
  reopen: (issueId: string) => Promise<void>;
}

export interface RunCollabState {
  collab: IssueCollab;
  participants: RunParticipant[];
  colorOf: (userId: string) => string;
  /** Participants other than me with a fresh heartbeat. */
  online: RunPresenceEntry[];
  canManage: boolean;
  shared: boolean;
  refresh: () => Promise<void>;
  invite: (email: string) => Promise<void>;
  remove: (userId: string) => Promise<void>;
}

/**
 * Live collaboration state for one finished run. Solo runs use plain REST; the SSE stream and
 * heartbeat only start once someone else is on the run.
 */
export function useRunCollab({
  runId,
  enabled,
  setIssues,
  onRemoved,
}: {
  runId: string | null;
  enabled: boolean;
  setIssues: Dispatch<SetStateAction<Issue[]>>;
  /** The caller was removed from the run by its owner. */
  onRemoved: () => void;
}): RunCollabState {
  const [me, setMe] = useState<string>();
  const [participants, setParticipants] = useState<RunParticipant[]>([]);
  const [presence, setPresenceState] = useState<Record<string, TrackedPresence>>({});
  const [canManage, setCanManage] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const claimRef = useRef<string | null>(null);
  const [myClaim, setMyClaim] = useState<string | null>(null);
  const onRemovedRef = useRef(onRemoved);
  onRemovedRef.current = onRemoved;
  const meRef = useRef<string>();
  meRef.current = me;
  const snapshotsSeenRef = useRef(0);

  const shared = participants.length > 1;
  const active = enabled && runId !== null;

  const applyPresence = useCallback((entries: RunPresenceEntry[], replace: boolean) => {
    const receivedAt = Date.now();
    setPresenceState((prev) => {
      const next: Record<string, TrackedPresence> = replace ? {} : { ...prev };
      for (const entry of entries) next[entry.userId] = { ...entry, receivedAt };
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!runId) return;
    const snapshot = await fetchCollab(runId);
    setParticipants(snapshot.participants);
    setCanManage(snapshot.canManage ?? false);
    applyPresence(snapshot.presence, true);
  }, [runId, applyPresence]);

  const applyIssueUpdate = useCallback(
    (issueId: string, status: string, resolution: IssueResolution | null) => {
      setIssues((prev) => prev.map((i) => (i.id === issueId ? { ...i, status: status as Issue['status'], resolution: resolution ?? undefined } : i)));
    },
    [setIssues],
  );

  useEffect(() => {
    if (!active || !runId) return;
    let cancelled = false;
    void fetchMyUserId().then((id) => !cancelled && setMe(id)).catch(() => undefined);
    void refresh().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [active, runId, refresh]);

  useEffect(() => {
    if (!active || !runId || !shared) return;
    snapshotsSeenRef.current = 0;
    const handle = (event: RunLiveEvent) => {
      switch (event.type) {
        case 'snapshot':
          setParticipants(event.snapshot.participants);
          applyPresence(event.snapshot.presence, true);
          // Events fired while disconnected are gone, so a reconnect must refetch the issues.
          if (snapshotsSeenRef.current++ > 0) void fetchIssues(runId).then(setIssues).catch(() => undefined);
          break;
        case 'issue.updated':
          applyIssueUpdate(event.issueId, event.status, event.resolution);
          break;
        case 'presence.updated':
          applyPresence([event.presence], false);
          break;
        case 'participants.changed':
          if (event.removedUserId && event.removedUserId === meRef.current) onRemovedRef.current();
          else void refresh().catch(() => undefined);
          break;
        case 'resync':
          void refresh().catch(() => undefined);
          void fetchIssues(runId).then(setIssues).catch(() => undefined);
          break;
      }
    };
    return subscribeToRunLive(runId, handle);
  }, [active, runId, shared, applyPresence, applyIssueUpdate, refresh, setIssues]);

  // Heartbeat while the pane is visible; a hidden pane goes quiet and ages out as offline.
  useEffect(() => {
    if (!active || !runId || !shared) return;
    const beat = () => {
      if (!document.hidden) void setPresence(runId, claimRef.current).catch(() => undefined);
    };
    beat();
    const interval = setInterval(beat, PRESENCE_HEARTBEAT_MS);
    return () => {
      clearInterval(interval);
      void setPresence(runId, null).catch(() => undefined);
    };
  }, [active, runId, shared]);

  // Re-evaluates who is still online between events.
  useEffect(() => {
    if (!shared) return;
    const interval = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(interval);
  }, [shared]);

  const fresh = useMemo(
    () => Object.values(presence).filter((p) => p.userId !== me && now - p.receivedAt < PRESENCE_TTL_MS),
    [presence, me, now],
  );

  const claim = useCallback(
    (issueId: string | null) => {
      if (claimRef.current === issueId) return;
      claimRef.current = issueId;
      setMyClaim(issueId);
      if (runId && shared) void setPresence(runId, issueId).catch(() => undefined);
    },
    [runId, shared],
  );

  const resolveOrReopen = useCallback(
    async (issueId: string, status: 'Fixed' | 'Fail', method?: ResolutionMethod) => {
      if (!runId) return;
      const result = await updateIssueStatus(runId, issueId, status, method);
      applyIssueUpdate(issueId, result.status, result.resolution);
      if (claimRef.current === issueId) claim(null);
    },
    [runId, applyIssueUpdate, claim],
  );

  const colorOf = useCallback(
    (userId: string) => participantColor(participants.map((p) => p.userId), userId),
    [participants],
  );

  const collab = useMemo<IssueCollab>(
    () => ({
      me,
      myClaim,
      claimedByOthers: (issueId) => fresh.filter((p) => p.activeIssueId === issueId),
      colorOf,
      claim,
      resolve: (issueId, method) => resolveOrReopen(issueId, 'Fixed', method),
      reopen: (issueId) => resolveOrReopen(issueId, 'Fail'),
    }),
    [me, myClaim, fresh, colorOf, claim, resolveOrReopen],
  );

  const invite = useCallback(
    async (email: string) => {
      if (!runId) return;
      await inviteCollaborator(runId, email);
      await refresh();
    },
    [runId, refresh],
  );

  const remove = useCallback(
    async (userId: string) => {
      if (!runId) return;
      await removeCollaborator(runId, userId);
      if (userId === meRef.current) onRemovedRef.current();
      else await refresh();
    },
    [runId, refresh],
  );

  return { collab, participants, colorOf, online: fresh, canManage, shared, refresh, invite, remove };
}
